import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { MENU, MenuItem } from './menu-items';
import { AuthService } from '../../core/services/auth.service';
import { Permission, PERMISSIONS_BY_ROLE } from '../../core/auth/permissions';
import { normalizeRole, ERole } from '../../core/auth/roles';
import { combineLatest, map } from 'rxjs';
import { ModeService } from '../../core/services/mode.service';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-topbar-menu',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './topbarmenu.component.html',
  styleUrls: ['./topbarmenu.component.scss'],
})
export class TopBarMenuComponent {
  private auth = inject(AuthService);
  private mode = inject(ModeService);
  private router = inject(Router);

  private mode$ = toObservable(this.mode.mode);

  vm$ = combineLatest([this.auth.isAuthenticated$, this.auth.userProfile$, this.mode$]).pipe(
    map(([isAuth, profile, uiMode]) => {
      const role = isAuth ? normalizeRole(profile?.role ?? null) : null;
      // Both Group Admin (org-scoped) and Sub-Group Admin get the Learner/Admin toggle.
      const isGA = role === ERole.GroupAdmin || role === ERole.SubGroupAdmin;
      const learnerModeForGA = isGA && uiMode === 'learner';

      const learnerRoutes = new Set<string>(['/dashboard', '/my-achievements', '/course-catalog', '/profile']);

      // fixed order in learner-mode for GA
      const learnerOrder: Record<string, number> = {
        '/dashboard': 10,
        '/my-achievements': 20,
        '/course-catalog': 30,
        '/profile': 40,
      };

      const filtered = (
        isAuth
          ? MENU.filter((i) => {
              if (i.guestOnly) return false;

              if (learnerModeForGA) {
                // For GA in learner-mode, show only these tabs
                return learnerRoutes.has(i.route);
              }

              if (i.onlyRoles?.length) {
                if (!role || !i.onlyRoles.includes(role)) return false;
              }
              if (i.excludeRoles?.length && role && i.excludeRoles.includes(role)) return false;
              if (!i.anyPerm?.length) return true;
              return this.hasAnyPermSync(role, i.anyPerm);
            })
          : MENU.filter((i) => i.guestOnly)
      ).map((i) => this.withRoleLabel(i, role));

      const items = learnerModeForGA
        ? [...filtered].sort((a, b) => (learnerOrder[a.route] ?? 9999) - (learnerOrder[b.route] ?? 9999))
        : [...filtered].sort((a, b) => this.getOrder(a, role) - this.getOrder(b, role));

      return items;
    }),
  );

  private hasAnyPermSync(role: ERole | null, perms: Permission[]): boolean {
    if (!role) return false;
    const granted = PERMISSIONS_BY_ROLE[role];
    return perms.some((p) => granted.has(p));
  }

  private withRoleLabel(item: MenuItem, role: ERole | null): MenuItem {
    const lbl = role ? (item.labelByRole?.[role] ?? null) : null;
    return lbl ? { ...item, label: lbl } : item;
  }

  navState(item: MenuItem) {
    const link = item.route || '';
    return link === '/groups' ? { resetGroupsList: true } : void 0;
  }

  private getOrder(item: MenuItem, role: ERole | null): number {
    if (role && item.orderByRole && item.orderByRole[role] != null) {
      return item.orderByRole[role]!;
    }
    return item.order ?? 1000;
  }

  isMenuItemActive(item: MenuItem): boolean {
    const currentUrl = this.router.url;

    // Special handling for User Management to include create/edit user routes
    if (item.route === '/user-management') {
      return currentUrl === '/user-management' || currentUrl.startsWith('/create-user');
    }

    // Special handling for Course Catalog to include course details routes
    if (item.route === '/course-catalog') {
      return (
        currentUrl.startsWith('/course-catalog') ||
        currentUrl.startsWith('/course-details') ||
        currentUrl.startsWith('/learning-path-details')
      );
    }

    // Default behavior for other menu items
    return currentUrl.startsWith(item.route);
  }
}
